import { useState } from "react";
import { Col, Form, InputGroup, Modal, Row } from "react-bootstrap";
import { Formik, Form as FormikForm, Field } from "formik";
import * as yup from "yup";
import FlashLogo from "../../../../Images/FlashLogo.png";
import GradientButton from "../../../Core/GradientButton";
import { useSessionStore } from "../../../../Stores/SessionStore";
import FlashPaymentAlertModal from "./FlashPaymentAlertModal";

const validationSchema = yup
	.object({
		PhoneNumber: yup.string().required("WhatsApp Number is required.").matches(/^\d{9,10}$/, "Phone number is not valid."),
	})
	.required();

function getInitialPhoneNumber(cellphoneNumber) {
	if (!cellphoneNumber) {
		return "";
	}

	const digits = cellphoneNumber.replace(/\D/g, "");
	if (digits.startsWith("27") && digits.length >= 11) {
		return digits.substring(2);
	}

	if (digits.startsWith("0") && digits.length === 10) {
		return digits.substring(1);
	}

	return digits.length > 10 ? digits.slice(-9) : digits;
}

export default function FlashAccountPaymentModal({ show, onClose, category, product, amount, voucher }) {
	const store = useSessionStore();
	const [showAlertModal, setShowAlertModal] = useState(false);
	const [alertData, setAlertData] = useState({ title: "", message: "", code: "", phoneNumber: "", isExisting: false, packageName: "" });
	const [sendingCode, setSendingCode] = useState(false);

	const phoneNumber = getInitialPhoneNumber(store.Session?.Customer?.CellphoneNumber);

	async function handleSendCode(values) {
		setSendingCode(true);
		const results = await store.RechargesService.InitiateFlashAccountPayment(values.PhoneNumber, product.Id, voucher?.Id);

		setSendingCode(false);
		setShowAlertModal(true);

		if (results && results.Success) {
			setAlertData({
				title: "Success",
				code: results.Data.Code,
				phoneNumber: values.PhoneNumber,
				isExisting: results.Data.IsExisting,
				packageName: results.Data.PackageName,
			});
			return;
		}

		setAlertData({ title: "Error", message: results?.Message ?? "Unable to generate Flash payment code." });
	}

	function handleAlertClose() {
		setShowAlertModal(false);
		onClose();
	}

	return (
		<>
			<Modal show={show} onHide={onClose} size="lg">
				<Modal.Header>
					<div className="w-100 text-center">
						<img src={FlashLogo} alt="Flash" style={{ height: 44, width: "auto" }} />
					</div>
				</Modal.Header>
				<Modal.Body>
					<div className="text-center bg-white p-3 mb-3">
						<p className="mb-0">
							Pay at a participating Flash retailer using your Flash payment code. Your code will be sent to you via SMS and WhatsApp.
						</p>
					</div>
					<p className="text-center font-weight-bold">
						{category} {product.Name} Access (R {amount.toFixed(0)})
					</p>
					<Formik validationSchema={validationSchema} initialValues={{ PhoneNumber: phoneNumber }} onSubmit={handleSendCode}>
						{({ errors, touched }) => (
							<FormikForm>
								<Form.Group as={Row} className="mb-3">
									<Col sm={12}>
										<Form.Label>WhatsApp Number</Form.Label>
										<InputGroup>
											<InputGroup.Text>+27</InputGroup.Text>
											<Field
												isInvalid={touched.PhoneNumber && errors.PhoneNumber}
												name="PhoneNumber"
												as={Form.Control}
												inputMode="numeric"
												maxLength={10}
											/>
										</InputGroup>
										<small className="text-danger">{touched.PhoneNumber && errors.PhoneNumber}</small>
									</Col>
								</Form.Group>
								<GradientButton
									title={sendingCode ? "Sending..." : "Send"}
									variant="greenYellow"
									type="submit"
									disabled={sendingCode}
									loading={sendingCode}
								/>
								<GradientButton
									title="Cancel Transaction"
									variant="yellowOrange"
									type="button"
									onClick={onClose}
									disabled={sendingCode}
									className="mt-2"
								/>
							</FormikForm>
						)}
					</Formik>
					<p className="text-center mt-3 mb-0">
						Flash Account Payments are for payments made directly at participating Flash retailers.
					</p>
				</Modal.Body>
			</Modal>
			<FlashPaymentAlertModal
				show={showAlertModal}
				onClose={handleAlertClose}
				title={alertData.title}
				code={alertData.code}
				phoneNumber={alertData.phoneNumber}
				message={alertData.message}
				isExisting={alertData.isExisting}
				packageName={alertData.packageName}
			/>
		</>
	);
}
